export type DistanceUnit = 'mi' | 'km'

export interface GeolocationCoords {
  latitude: number
  longitude: number
  accuracy?: number
  altitude?: number
  altitudeAccuracy?: number
  heading?: number
}

/**
 * What a schema author can hand to `destination`: an `address` object (the
 * `entity.json#/definitions/address` shape), anything else carrying a
 * `geolocation`, or a bare coordinate pair.
 */
export type AddressLike =
  | GeolocationCoords
  | { geolocation?: GeolocationCoords | null }
  | null
  | undefined

const EARTH_RADIUS_METERS = 6371000
const METERS_PER_MILE = 1609.344
const METERS_PER_KILOMETER = 1000

export function hasCoords(value: unknown): value is GeolocationCoords {
  const coords = value as GeolocationCoords | undefined
  return (
    !!coords &&
    typeof coords.latitude === 'number' &&
    typeof coords.longitude === 'number' &&
    isFinite(coords.latitude) &&
    isFinite(coords.longitude)
  )
}

/** Unwraps an address's `geolocation`, or passes a bare coordinate pair through. */
export function resolveDestinationGeolocation(destination: AddressLike): GeolocationCoords | undefined {
  if (!destination) return undefined
  const nested = (destination as { geolocation?: GeolocationCoords | null }).geolocation
  if (hasCoords(nested)) return nested
  if (hasCoords(destination)) return destination
  return undefined
}

/** Great-circle distance in meters. */
export function haversineDistance(from: GeolocationCoords, to: GeolocationCoords): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180
  const dLat = toRadians(to.latitude - from.latitude)
  const dLon = toRadians(to.longitude - from.longitude)
  const lat1 = toRadians(from.latitude)
  const lat2 = toRadians(to.latitude)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)
  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/**
 * Distance rounded for reading, not for navigation: one decimal under 10
 * units, whole numbers above. Sub-tenth distances collapse to `Less than
 * 0.1 mi` rather than `0 mi`, which reads as "no distance at all".
 */
export function formatDistance(meters: number, unit: DistanceUnit = 'mi'): string {
  const perUnit = unit === 'km' ? METERS_PER_KILOMETER : METERS_PER_MILE
  const converted = meters / perUnit
  if (converted < 0.1) return `Less than 0.1 ${unit}`
  const rounded = converted < 10 ? converted.toFixed(1) : String(Math.round(converted))
  return `${rounded} ${unit}`
}

/**
 * Distance between a captured position and a configured destination, already
 * formatted. Undefined when either side is missing — callers use that to
 * decide whether there is anything to show at all.
 */
export function resolveDistanceText(
  position: GeolocationCoords | undefined,
  destination: AddressLike,
  unit: DistanceUnit = 'mi',
): string | undefined {
  const target = resolveDestinationGeolocation(destination)
  if (!hasCoords(position) || !target) return undefined
  return formatDistance(haversineDistance(position, target), unit)
}

/**
 * Maps link for the destination — the useful thing to open is where the user
 * is meant to be, not where they already are.
 */
export function buildMapsUrl(coords: GeolocationCoords): string {
  return `https://www.google.com/maps/search/?api=1&query=${coords.latitude},${coords.longitude}`
}
